/**
 * ReportInputCard — the one surface the tester uses to file a report online.
 * Shared by the between-jobs gate and the Test Queue tab so the "let's knock it
 * out" card and the nudge are literally the same component.
 *
 * Everything they need is here: readings, tap-to-copy account/serial/license,
 * download photos, the portal deep link, a per-portal cheat sheet, and the three
 * honest doors — do it now (Mark submitted), a real city hold, and the
 * "Can't right now" escape hatch (hidden only when the office has forced it).
 *
 * #1100 — behind `field_redesign_v2` the middle of the card is grouped into the
 * order the work is actually done in (numbers → photos → portal), and the photo
 * block becomes the real hand-off: see `portal-photo-handoff.tsx` for why a zip
 * is the wrong shape on a phone. Flag off keeps today's card exactly, zip and
 * all — pinned by a test, per the rollback promise in ROLLOUT.md.
 */
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { openExternalLink } from "@/lib/standalone";
import { attachmentsZipUrl, downloadAttachmentFile } from "@/lib/api";
import { PortalPhotoHandoff } from "@/components/portal-photo-handoff";
import { type ReportInputPacket, markSubmitted, snoozeReport, requestCityHold, hoursSince } from "@/lib/report-input";
import { cityFilingIdentity } from "@/pages/field/redesign-screens-copy";
import { ExternalLink, Copy, Check, Download, ClipboardList, Clock, MapPinOff, Coffee } from "lucide-react";

type Variant = "wall" | "nudge" | "queue";

interface Props {
    packet: ReportInputPacket;
    variant: Variant;
    onResolved: () => void;
    onDismiss?: () => void;
    /**
     * #950 SPEC C4/C5 — the redesigned presentation, behind `field_redesign_v2`.
     * Two things change and nothing else: the card says whether this city is a
     * portal or a paper city (`isPortal` was fetched and never shown), and the
     * "Can't right now" door comes back in the queue variant.
     */
    redesign?: boolean;
}

function CopyField({ label, value }: { label: string; value: string }) {
    const [copied, setCopied] = useState(false);
    return (
        <button
            type="button"
            onClick={() => {
                try {
                    void navigator.clipboard?.writeText(value);
                } catch {
                    /* clipboard may be blocked; the visual state is the feedback */
                }
                setCopied(true);
                setTimeout(() => setCopied(false), 1400);
            }}
            className="flex w-full items-center justify-between gap-3 rounded-lg border border-border bg-muted/40 px-3 py-2 text-left hover:border-primary"
        >
            <span>
                <span className="block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {label}
                </span>
                <span className="block text-sm font-semibold tabular-nums">{value}</span>
            </span>
            <span
                className={
                    "inline-flex items-center gap-1 text-[11px] font-semibold " +
                    (copied ? "text-emerald-600" : "text-primary")
                }
            >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? "Copied" : "Tap to copy"}
            </span>
        </button>
    );
}

/**
 * One numbered group inside the redesigned card. The numbers are honest here:
 * this really is a sequence — you cannot paste an account number you haven't
 * copied, and you cannot upload a photo that is still only in the job.
 */
function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
    return (
        <div className="flex flex-col gap-2.5 rounded-xl border border-border p-3">
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                <span className="inline-flex h-[17px] w-[17px] shrink-0 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground tabular-nums">
                    {n}
                </span>
                {title}
            </div>
            {children}
        </div>
    );
}

export function ReportInputCard({ packet, variant, onResolved, onDismiss, redesign }: Props) {
    const [mode, setMode] = useState<"idle" | "submit" | "snooze" | "cityhold">("idle");
    const [reason, setReason] = useState("");
    const [confirmation, setConfirmation] = useState("");
    const [showSteps, setShowSteps] = useState(false);

    const hrs = hoursSince(packet.testAt);
    const portalTarget = packet.deepLink || packet.portalUrl;
    /**
     * C5 — the honest exit, restored.
     *
     * The queue variant hid "Can't right now" entirely, so a tester who
     * genuinely couldn't file (no signal, on a ladder, portal down) had two
     * doors: claim they'd submitted it, or close the card and leave the office
     * guessing. Neither is honest. A real city block already had its own door;
     * this is the everyday one, and it tells the office why. Still never shown
     * when the office has forced the report in.
     */
    const canSnooze = !packet.gateForced && (redesign || variant !== "queue");

    const submit = useMutation({
        mutationFn: () => markSubmitted(packet.jobId, confirmation),
        onSuccess: () => {
            toast.success("Nice — that's in 👍");
            onResolved();
        },
        onError: () => toast.error("Couldn't record that — try again in a sec."),
    });
    const snooze = useMutation({
        mutationFn: () => snoozeReport(packet.jobId, reason),
        onSuccess: () => {
            toast.success("Okay — we told the office. On you go.");
            onResolved();
        },
        onError: (e: unknown) => {
            const forced = e && typeof e === "object" && "status" in e && (e as { status?: number }).status === 409;
            toast.error(forced ? "The office needs this one in — no snooze on it." : "Couldn't save that.");
        },
    });
    const cityHold = useMutation({
        mutationFn: () => requestCityHold(packet.jobId, reason),
        onSuccess: () => {
            toast.success("Flagged for the office to confirm. You're good to move on.");
            onResolved();
        },
        onError: () => toast.error("Couldn't flag that — try again."),
    });

    const busy = submit.isPending || snooze.isPending || cityHold.isPending;

    /**
     * What the stamp actually burns into the pixels — serial and the device's
     * size/type, per `portalPhotos.ts`. Named here rather than described, so
     * the tech knows what the city will be looking at.
     */
    const stampLabel = [packet.serialNumber, packet.deviceLabel].filter(Boolean).join(" · ") || null;

    const copyFields = (
        <div className="flex flex-col gap-2">
            {packet.accountNumber && <CopyField label="Account #" value={packet.accountNumber} />}
            {packet.serialNumber && <CopyField label="Serial" value={packet.serialNumber} />}
            {packet.testerLicense && <CopyField label="Tester license" value={packet.testerLicense} />}
        </div>
    );

    /** Today's card, kept intact for flag-off. */
    const legacyPhotos = packet.photos.length > 0 && (
        <div className="flex flex-wrap gap-2">
            <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => openExternalLink(attachmentsZipUrl(packet.photos.map((p) => p.id)))}
            >
                <Download className="mr-1.5 h-4 w-4" />
                Download all {packet.photos.length}
            </Button>
            {packet.photos.slice(0, 4).map((p, i) => (
                <Button
                    key={p.id}
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => void downloadAttachmentFile(p.id, p.fileName ?? undefined)}
                >
                    <Download className="mr-1.5 h-4 w-4" />
                    {p.fileName?.slice(0, 14) ?? `Photo ${i + 1}`}
                </Button>
            ))}
        </div>
    );

    const cheatSheet = (
        <div className="flex flex-col gap-1.5">
            <button
                type="button"
                onClick={() => setShowSteps((v) => !v)}
                className="flex items-center gap-1.5 text-left text-xs font-semibold text-primary"
            >
                <ClipboardList className="h-3.5 w-3.5" />
                {showSteps ? "Hide" : "How to file at"} {packet.cheatSheet.platform}
            </button>
            {showSteps && (
                <ol className="ml-4 list-decimal space-y-1 text-xs text-muted-foreground">
                    {packet.cheatSheet.steps.map((step, i) => (
                        <li key={i}>{step}</li>
                    ))}
                </ol>
            )}
        </div>
    );

    const portalButton = (
        <Button
            type="button"
            disabled={!portalTarget || busy}
            onClick={() => portalTarget && openExternalLink(portalTarget)}
        >
            <ExternalLink className="mr-1.5 h-4 w-4" />
            Open {packet.platform ?? "portal"} &amp; input results
        </Button>
    );

    return (
        <div className="flex flex-col gap-3">
            {/* header */}
            <div className="flex items-start justify-between gap-2">
                <div>
                    <div className="text-base font-bold leading-tight">{packet.siteName ?? "Job"}</div>
                    <div className="text-xs text-muted-foreground">
                        {[packet.city, packet.deviceLabel, packet.serialNumber && `SN ${packet.serialNumber}`]
                            .filter(Boolean)
                            .join(" · ")}
                    </div>
                    {/* C4 — `isPortal` was fetched on every packet and shown
                        nowhere, so a paper city looked identical to a portal
                        city right up until the tech tapped a dead button. */}
                    {redesign && (
                        <div className="text-xs text-foreground/70" data-testid="report-city-identity">
                            {cityFilingIdentity({ isPortal: packet.isPortal, platform: packet.platform })}
                        </div>
                    )}
                </div>
                {hrs != null && (
                    <span
                        className={
                            "inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] font-bold tabular-nums " +
                            (hrs >= 36
                                ? "border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300"
                                : "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300")
                        }
                    >
                        <Clock className="h-3 w-3" />
                        {hrs}h
                    </span>
                )}
            </div>

            {/* office reason */}
            {packet.reason && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs dark:border-amber-900 dark:bg-amber-950/50">
                    “{packet.reason}” <span className="text-muted-foreground">— office</span>
                </div>
            )}

            {/* readings */}
            {(packet.cv1 || packet.cv2 || packet.result) && (
                <div className="grid grid-cols-3 gap-2">
                    {[
                        { lab: "CV1", val: packet.cv1 },
                        { lab: "CV2", val: packet.cv2 },
                        { lab: "Result", val: packet.result },
                    ].map((c) => (
                        <div key={c.lab} className="rounded-lg border border-border bg-muted/40 px-2.5 py-2">
                            <div className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
                                {c.lab}
                            </div>
                            <div className="text-sm font-bold tabular-nums">{c.val ?? "—"}</div>
                        </div>
                    ))}
                </div>
            )}

            {redesign ? (
                <>
                    <Step n={1} title="Numbers to paste">
                        {copyFields}
                    </Step>
                    <Step n={2} title="Photos for the upload">
                        <PortalPhotoHandoff photos={packet.photos} stampLabel={stampLabel} />
                    </Step>
                    <Step n={3} title="File it">
                        {cheatSheet}
                        {portalButton}
                    </Step>
                </>
            ) : (
                <>
                    {copyFields}
                    {legacyPhotos}
                    {cheatSheet}
                </>
            )}

            {/* ── actions ── */}
            {mode === "idle" && (
                <div className="mt-1 flex flex-col gap-2">
                    {/* #1100 — under the flag this sits in step 3, beside the
                        steps for that city, instead of below a cheat sheet the
                        tech hasn't opened yet. */}
                    {!redesign && portalButton}
                    <Button type="button" variant="secondary" disabled={busy} onClick={() => setMode("submit")}>
                        <Check className="mr-1.5 h-4 w-4" />
                        Mark submitted
                    </Button>
                    <Button type="button" variant="ghost" disabled={busy} onClick={() => setMode("cityhold")}>
                        <MapPinOff className="mr-1.5 h-4 w-4" />
                        City won&rsquo;t let me register yet
                    </Button>
                    {canSnooze && (
                        <Button type="button" variant="ghost" disabled={busy} onClick={() => setMode("snooze")}>
                            <Coffee className="mr-1.5 h-4 w-4" />
                            Can&rsquo;t right now
                        </Button>
                    )}
                    {variant === "nudge" && onDismiss && (
                        <Button type="button" variant="ghost" className="text-muted-foreground" onClick={onDismiss}>
                            Not right now
                        </Button>
                    )}
                    {variant === "queue" && onDismiss && (
                        <Button type="button" variant="ghost" className="text-muted-foreground" onClick={onDismiss}>
                            Close
                        </Button>
                    )}
                    {packet.gateForced && (
                        <p className="text-center text-[11px] text-muted-foreground">
                            The office needs this one in — a real city block still gets you through.
                        </p>
                    )}
                </div>
            )}

            {mode === "submit" && (
                <div className="mt-1 flex flex-col gap-2">
                    <label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Confirmation # (optional)
                    </label>
                    <Input
                        value={confirmation}
                        onChange={(e) => setConfirmation(e.target.value)}
                        placeholder="e.g. VEPO-2026-118377"
                    />
                    <Button type="button" disabled={submit.isPending} onClick={() => submit.mutate()}>
                        Confirm submitted
                    </Button>
                    <Button type="button" variant="ghost" onClick={() => setMode("idle")}>
                        Back
                    </Button>
                </div>
            )}

            {(mode === "snooze" || mode === "cityhold") && (
                <div className="mt-1 flex flex-col gap-2">
                    <label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        {mode === "snooze"
                            ? "What's got you tied up? (we'll tell the office)"
                            : "What's the city block?"}
                    </label>
                    <Input
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder={
                            mode === "snooze"
                                ? "e.g. slammed, I'll do it tonight"
                                : "e.g. not registered with this city yet"
                        }
                    />
                    <Button
                        type="button"
                        disabled={!reason.trim() || busy}
                        onClick={() => (mode === "snooze" ? snooze.mutate() : cityHold.mutate())}
                    >
                        {mode === "snooze" ? "Send & continue" : "Flag city block & continue"}
                    </Button>
                    <Button type="button" variant="ghost" onClick={() => setMode("idle")}>
                        Back
                    </Button>
                </div>
            )}
        </div>
    );
}
